import api from '@/api/account'
import i18n from '@/i18n'
import router from '@/router/routes'
import Optional from 'optional-js'
import Cookies from 'js-cookie'
import _ from 'lodash'
import { HEADHUNTER_DISABLED_TABS } from '@/constant/permission'
import { CHAT_BOT_COOKIE_USER_UUID_KEY_MAP } from '@/constant/chatbot'

const state = {
  currentAccount: {},
  currentSoftUser: {},
  allPermission: [],
  accountConfig: {}
}

const actions = {
  async getSessionAccountInfo (context) {
    const resp = await api.getSessionAccountInfo()
    if (resp) {
      context.commit('SET_CURRENT_ACCOUNT', resp)
      context.commit('SET_CURRENT_SOFT_USER', resp.soft_user)
    }
    const currentAccount = context.state.currentAccount
    return currentAccount.id ? currentAccount : null
  },
  async getSessionAccountInfoWithWxAutoLogin (context, params) {
    const resp = await api.getSessionAccountInfo(params)
    if (resp) {
      if (resp.wx_oauth_url) {
        return resp.wx_oauth_url
      } else if (resp.wecom_oauth_url) {
        return resp.wecom_oauth_url
      } else {
        context.commit('SET_CURRENT_ACCOUNT', resp)
        context.commit('SET_CURRENT_SOFT_USER', resp.soft_user)
      }
    }
  },
  clearAccountInfo ({ commit }) {
    commit('SET_CURRENT_ACCOUNT', null)
  },
  async doCompanyLogin ({ commit }, params) {
    const resp = await api.doCompanyLogin(params)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  getCompanyEmployeeRegisterInfo ({ commit }, { companyId, params }) {
    return api.getCompanyEmployeeRegisterInfo(companyId, params)
  },
  async registerCompanyEmployee ({ commit }, { companyId, employee }) {
    const resp = await api.registerCompanyEmployee(companyId, employee)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  sendSmsValidateCode ({ commit }, params) {
    return api.sendSmsValidateCode(params)
  },
  async doCandidateRegister ({ commit }, params) {
    const resp = await api.doCandidateRegister(params)
    if (resp && resp) {
      if (resp.account) {
        commit('SET_CURRENT_ACCOUNT', resp.account)
      }
    }
    return resp
  },
  forgetPassword ({ commit }, params) {
    return api.forgetPassword(params)
  },
  updateAccountMobile ({ commit }, params) {
    return api.updateAccountMobile(params)
  },
  getAccountPageConfigList (context, params) {
    return api.getAccountPageConfigList(params)
  },
  createAccountPageConfig (context, pageConfig) {
    return api.createAccountPageConfig(pageConfig)
  },
  updateAccountPageConfig (context, pageConfig) {
    return api.updateAccountPageConfig(pageConfig)
  },
  deleteAccountPageConfig (context, pageConfig) {
    return api.deleteAccountPageConfig(pageConfig)
  },
  getLoginInfo (context, params) {
    return api.getLoginInfo(params)
  },
  async login ({ commit }, accountInfo) {
    const resp = await api.login(accountInfo)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  checkSSO (context, accountInfo) {
    return api.checkSSO(accountInfo)
  },
  getThirdPartyLoginInfo (context, params) {
    return api.getThirdPartyLoginInfo(params)
  },
  async getLogonAccountNextPage ({ commit }, params) {
    const resp = await api.getLogonAccountNextPage(params)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  async registerEmployee ({ commit }, { companyId, employeeInfo }) {
    const resp = await api.registerEmployee(companyId, employeeInfo)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  getCreatePasswordCodeInfo (context, params) {
    return api.getCreatePasswordCodeInfo(params)
  },
  async createPassword ({ commit }, info) {
    const resp = await api.createPassword(info)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  getResetPasswordCodeInfo (context, params) {
    return api.getResetPasswordCodeInfo(params)
  },
  async resetPassword ({ commit }, info) {
    const resp = await api.resetPassword(info)
    if (resp && resp.account) {
      commit('SET_CURRENT_ACCOUNT', resp.account)
    }
    return resp
  },
  inviteConfirmChangeEmail (context, params) {
    return api.inviteConfirmChangeEmail(params)
  },
  setAccountConfig ({ commit }, accountConfig) {
    commit('SET_ACCOUNT_CONFIG', accountConfig)
  }
}

const mutations = {
  SET_CURRENT_ACCOUNT (state, accountInfo) {
    const currentAccount = Optional.ofNullable(accountInfo).map(item => item).orElse({})
    if (!router.currentRoute.path.startsWith('/onboarding')) {
      if (currentAccount.hasOwnProperty('language')) {
        i18n.locale = currentAccount.language === 'en' ? 'en' : 'zh'
      }
    }
    currentAccount.full_name = currentAccount.full_name || `${currentAccount.first_name || ''} ${currentAccount.last_name || ''}`
    state.currentAccount = currentAccount
    if (currentAccount && currentAccount.id) {
      state.allPermission = [
        ...currentAccount.permissions || [],
        ...currentAccount.premiums || [],
        ...currentAccount.computed_permissions || []
      ]
    } else {
      state.allPermission = []
    }
  },
  SET_CURRENT_SOFT_USER (state, softUserInfo) {
    const currentSoftUser = Optional.ofNullable(softUserInfo).map(item => item).orElse({})
    if (currentSoftUser && currentSoftUser.id) {
      state.currentSoftUser = currentSoftUser
    } else {
      state.currentSoftUser = {}
    }
  },
  SET_ACCOUNT_CONFIG (state, accountConfig) {
    state.accountConfig = accountConfig
  }
}

const getters = {
  getUserUUID: (state) => (rootCompanyId) => {
    let key = CHAT_BOT_COOKIE_USER_UUID_KEY_MAP[rootCompanyId]
    if (!key) {
      key = `user_id_${rootCompanyId}`
    }
    return Cookies.get(key)
  },
  isCandidateAccount: (state) => {
    return state.currentAccount.type === 'candidate'
  },
  isCompanyAccount: (state) => {
    return state.currentAccount.type === 'company'
  },
  isHeadhunterAccount: (state) => {
    return state.currentAccount.type === 'headhunter'
  },
  hasEmployeeAccountInfo: (state) => {
    return Boolean(state.currentAccount.employee)
  },
  getCurrentAccount: (state) => {
    return state.currentAccount
  },
  getCurrentSoftUser: (state) => {
    return state.currentSoftUser
  },
  accountConfig: (state) => {
    return state.accountConfig
  },
  /***
   * 判断当前账号权限收费项
   * @param permissions: object or array or string
   *
   */
  hasAnyPermission: (state, getters) => (permissions) => {
    if (!permissions) {
      return true
    }
    let isStrict = true
    let everyPermissions
    let employeePermission
    let isMatchOr = false
    let isMatchAnd = false
    if (typeof permissions === 'object' && !(permissions instanceof Array)) {
      const strict = permissions.strict
      if (strict === undefined) {
        isStrict = true
      } else if (strict === '') {
        isStrict = true
      } else {
        isStrict = Boolean(strict)
      }
      if (!isStrict && _.has(permissions, 'every')) {
        everyPermissions = permissions.every
        if (typeof everyPermissions === 'string') {
          everyPermissions = [everyPermissions]
        }
      }
      if (permissions.employee) {
        employeePermission = permissions.employee
      }
      isMatchOr = Boolean(permissions.isMatchOr)
      isMatchAnd = Boolean(permissions.isMatchAnd)
      permissions = permissions.any
    }
    if (typeof permissions === 'string') {
      permissions = [permissions]
    }
    if (typeof employeePermission === 'string') {
      employeePermission = [employeePermission]
    }
    if (getters.isHeadhunter) {
      for (const tabPermission of HEADHUNTER_DISABLED_TABS) {
        const result = _.intersection(tabPermission, permissions)
        if (result.length === permissions.length) {
          return false
        }
      }
    }
    if (isMatchOr || isMatchAnd) {
      // permissions 每项是收费项字符串数组，如果不是会变成一个数组
      const allPermission = state.allPermission
      permissions = _.map(permissions, item => {
        if (!_.isArray(item)) {
          return [item]
        }
        return item
      })
      const checkPremiumSubarray = (subarray) => {
        const stringSubarray = _.filter(subarray, item => typeof item === 'string')
        // isMatchAnd permissions 收费项子数组中收费项都在 allPermission
        // isMatchOr permissions 收费项子数组中收费项任意一个在 allPermission
        return isMatchAnd
          ? _.every(stringSubarray, item => _.includes(allPermission, item))
          : _.some(stringSubarray, item => _.includes(allPermission, item))
      }
      const results = _.map(permissions, item => checkPremiumSubarray(item))

      // 如果 isStrict 则要求 permissions 每个子数组的权限判断结果都为 true，否则 任意一个 true 即可
      const finalResult = isStrict
        ? _.every(results, Boolean)
        : _.some(results, Boolean)
      return finalResult
    } else {
      const result = _.intersection(state.allPermission, permissions)
      if (!result || result.length < 1) {
        return false
      }
      let everyPermissionResult = []
      if (everyPermissions && !isStrict) {
        everyPermissionResult = _.intersection(state.allPermission, everyPermissions)
      }

      if (isStrict) {
        return result.length === permissions.length
      } else if (!isStrict && everyPermissions && everyPermissions.length > 0) {
        if (!everyPermissionResult || everyPermissionResult.length < 1) {
          return false
        }
        return result.length > 0 && everyPermissionResult.length === everyPermissions.length
      } else {
        return result.length > 0
      }
    }
  },
  wxUserHasBindRewards: (state) => {
    return state.currentAccount.wx_user_has_bind_rewards
  }
}
export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
